import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { SEASONS } from '@app/activo2/manage-vacations/manage-vacations.constants';
import {
  IMomoCreateProposal,
  IProposal,
  UpdateProposal
} from '@app/activo2/manage-vacations/manage-vacations.interface';
import {
  MomoSeasonProposal,
  MomoVacationsProposal,
  VacationsSeason
} from '@app/activo2/manage-vacations/manage-vacations.model';
import { BaseManageVacationsPage } from '@app/activo2/manage-vacations/manage-vacations.page';
import { cloneDeep } from 'lodash';

import { CriteriaModalComponent } from './components/criteria-modal/criteria-modal.component';

@Component({
  selector: 'app-momo-manage-vacations',
  templateUrl: './manage-vacations.page.html',
  styleUrls: ['./manage-vacations.page.scss']
})
export class MomoManageVacationsPage implements OnInit {
  @Input() base!: BaseManageVacationsPage;
  @Output() createProposal: EventEmitter<UpdateProposal> = new EventEmitter<UpdateProposal>();

  ngOnInit() {
    this.updateButtonsTexts();
  }

  get stepperTitle() {
    let title = '';

    switch (this.base.currentStep) {
      case 1:
        title = 'MANAGE_VACATIONS.CHECK_VACATIONS_CRITERIA';
        break;
      case 2:
        title = 'MANAGE_VACATIONS.SELECT_PROPOSAL';
        break;
      case 3:
        title = 'MANAGE_VACATIONS.SELECT_PROPOSAL';
        break;
    }

    return title;
  }

  get showStepper() {
    return (
      (!this.base.proposals || !this.base.proposals.userHasData() || this.base.currentStep !== 4) &&
      this.base.hasEditPermission
    );
  }

  setSeason(selectedSeason: VacationsSeason) {
    this.base.selectedSeason = selectedSeason;
    this.base.notSelectedSeason = this.base.seasons.find((season) => selectedSeason.id !== season.id);
  }

  setSlots(slots: IProposal[]) {
    this.base.nextDisabled = Boolean(!slots.length);
    this.base.slots = slots;
    this.updateSeasonSlots();
  }

  editProposal(seasonProposal: MomoSeasonProposal) {
    this.updateCalendar(seasonProposal.summer);
    this.base.seasonProposal = seasonProposal;
    this.base.seasonProposal.setInitialWeight();
    this.base.currentStep = 2;
    this.base.backDisabled = false;
    this.base.nextDisabled = false;
    this.base.initialSlots = seasonProposal.summer.proposals;
    this.base.modifyingOrder = seasonProposal.order!;
    this.updateButtonsTexts();
    this.base.loadThirdStep(true);
  }

  async updateStep(goingForward: boolean) {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;
    this.base.proposals = this.base.proposals as MomoVacationsProposal;

    if (this.base.currentStep === 1) {
      this.base.initialSlots = [];
      this.base.slots = [];
      this.base.seasonProposal = new MomoSeasonProposal({ winter: [], summer: [] });
      this.base.seasonProposal.setProposalOrder(1);
    }

    if (this.base.currentStep === 2) {
      const isShowingResume = this.base.showResumeView;

      if (isShowingResume) {
        this.base.showResumeView = false;

        if (goingForward) {
          this.base.nextDisabled = true;
          this.base.slots = [];
          this.updateShowingSeasons();
          this.updateButtonsTexts();
          this.base.resetCalendar();
          this.updateStepperIndex(goingForward);
          return;
        }

        this.base.initialSlots = this.base.slots;
        return;
      } else if (goingForward) {
        // We are adding or editing a proposal
        if (this.base.modifyingOrder) {
          this.base.seasonProposal.setProposalOrder(this.base.modifyingOrder);
        }

        this.base.showResumeView = true;
        this.updateButtonsTexts();
        return;
      } else if (this.base.proposals) {
        // We pressed back on summer calendar view after click on edit
        this.base.currentStep = 4;
        this.base.proposals = cloneDeep(this.base.initialProposals);
        return;
      }
    } else if (this.base.currentStep === 3) {
      const isShowingResume = this.base.showResumeView;

      if (isShowingResume) {
        if (goingForward) {
          if (
            this.base.proposals &&
            this.base.proposals.proposalIsFullfilled() &&
            this.base.seasonProposal.checkDuplicatedProposals(this.getAddingProposals())
          ) {
            this.base.alertService.showWarning(
              this.base.messages['INFO_TOASTS.INFO_TITLE'],
              this.base.messages['MANAGE_VACATIONS.SLOT_ALREADY_ADDED']
            );
            return;
          }
          this.base.slots = [];
          this.base.initialSlots = [];
          this.base.resetCalendar();
          this.manageOrderedProposals();
          return;
        }

        this.base.showResumeView = false;
        this.base.initialSlots = this.base.slots;
        this.updateButtonsTexts();
        return;
      } else if (goingForward) {
        if (this.base.seasonProposal.checkSlotDurations(this.base.slots)) {
          this.base.alertService.showWarning(
            this.base.messages['INFO_TOASTS.INFO_TITLE'],
            this.base.messages['MANAGE_VACATIONS.SELECT_ANOTHER_WINTER_SLOT']
          );
          return;
        }

        // We are adding or editing a proposal
        this.base.showResumeView = true;
        this.updateButtonsTexts();
        return;
      }

      this.base.slots = this.base.seasonProposal.summer.proposals;
      this.base.showResumeView = true;
      this.updateShowingSeasons();
      this.base.resetCalendar();
    }

    this.updateStepperIndex(goingForward);
  }

  updateStepperIndex(goingForward: boolean) {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;

    if (goingForward) {
      this.base.currentStep++;
    } else {
      this.base.currentStep--;
    }

    this.base.isCalendarView = this.base.currentStep === 2 && !this.base.showResumeView;
    this.base.showInfoIcon = this.base.currentStep === 2;

    switch (this.base.currentStep) {
      case 1:
        this.base.backDisabled = Boolean(!this.base.proposals);
        this.updateButtonsTexts();
        break;
      case 2:
        this.base.initialSlots = this.base.slots;
        this.base.backDisabled = false;
        this.base.nextDisabled = Boolean(!this.base.seasonProposal.summer.proposals?.length);
        this.updateButtonsTexts();

        if (this.base.criteria) {
          this.setSeason(this.base.criteria.seasons[1]);
          this.base.loadThirdStep(false);
        }
        break;
      case 3:
        this.base.initialSlots = this.base.seasonProposal.winter.proposals;
        this.base.backDisabled = false;
        this.base.nextDisabled = Boolean(!this.base.seasonProposal.winter.proposals?.length);

        if (this.base.criteria) {
          this.setSeason(this.base.criteria.seasons[0]);
        }
        this.base.loadThirdStep(false);
        break;
      default:
        this.base.currentStep = 4;
    }
  }

  updateButtonsTexts() {
    if (this.base.currentStep === 1) {
      this.base.nextDisabled = false;
      this.base.nextButtonText = 'MANAGE_VACATIONS.PROPOSAL_1';
    } else {
      this.base.backButtonText = 'STEPPER.BACK';
      this.base.nextButtonText = 'STEPPER.NEXT';
    }

    if (this.base.showResumeView && this.base.currentStep === 3) {
      this.base.nextButtonText = 'MANAGE_VACATIONS.SEND_PROPOSAL';
    }

    this.base.isCalendarView =
      (this.base.currentStep === 2 || this.base.currentStep === 3) && !this.base.showResumeView;
    this.base.showInfoIcon = this.base.currentStep === 2 || this.base.currentStep === 3;
  }

  addProposals() {
    this.base.currentStep = 2;
    this.base.backDisabled = false;
    this.base.seasonProposal = new MomoSeasonProposal({ winter: [], summer: [] });
    this.base.seasonProposal.setProposalOrder(2);

    this.updateCalendar(this.base.seasonProposal.summer);
    this.base.loadThirdStep(false);
    this.updateButtonsTexts();
  }

  async onInfoIconClicked() {
    if (!this.base.criteria) {
      await this.base.getCriteria();
    }

    const modalOpts = {
      data: { criteria: this.base.criteria },
      disableClose: true,
      panelClass: 'media-content-modal'
    };

    this.base.modalManager.openMatModal(CriteriaModalComponent, modalOpts);
  }

  manageOrderedProposals() {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;
    this.base.proposals = this.base.proposals as MomoVacationsProposal;

    const proposals: IMomoCreateProposal = {
      preferences: [],
      observations: ''
    };

    if (this.base.modifyingOrder) {
      this.base.proposals.preferences.forEach((preference) => {
        proposals.preferences.push({
          winter: preference.winter.proposals,
          summer: preference.summer.proposals
        });
      });
    } else if (this.base.proposals) {
      proposals.preferences.push({
        winter: this.base.proposals.preferences[0].winter.proposals,
        summer: this.base.proposals.preferences[0].summer.proposals
      });
      this.addCurrentSelectedProposals(proposals);
    } else {
      this.addCurrentSelectedProposals(proposals);
    }

    proposals.observations = this.base.proposals ? this.base.proposals.observations : '';

    this.createProposal.emit({ proposals, deleting: false });
  }

  private addCurrentSelectedProposals(proposals: IMomoCreateProposal) {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;

    proposals.preferences.push({
      winter: this.base.seasonProposal.winter.proposals,
      summer: this.base.seasonProposal.summer.proposals
    });
  }

  private updateShowingSeasons() {
    const auxSelectedSeason = this.base.selectedSeason!;
    this.base.selectedSeason = this.base.notSelectedSeason!;
    this.base.notSelectedSeason = auxSelectedSeason;
  }

  private updateSeasonSlots() {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;

    if (this.base.selectedSeason.id === SEASONS.summer) {
      this.base.seasonProposal.addSummerProposals(this.base.slots);
    } else {
      this.base.seasonProposal.addWinterProposals(this.base.slots);
    }
  }

  private updateCalendar(seasonProposal) {
    this.base.setSelectedSeasonByProposal(seasonProposal);
    this.base.resetCalendar();
  }

  private getAddingProposals(): MomoSeasonProposal[] {
    this.base.seasonProposal = this.base.seasonProposal as MomoSeasonProposal;
    this.base.proposals = this.base.proposals as MomoVacationsProposal;

    const auxPreferences = [...this.base.proposals.preferences];

    if (this.base.proposals.preferences.length === 1 && !this.base.modifyingOrder) {
      auxPreferences.push(this.base.seasonProposal);
    }

    return auxPreferences;
  }
}
